import React, { useContext } from 'react';
import {
  BreakdownColumn,
  BreakdownRow,
} from 'spw-programming-projects/program-summary/components/styles';
import { Box, Grid } from '@mui/material';
import {
  SmallCard,
  SmallCardTitle,
} from 'spw-components/project-details-tabs/program-summary/esa-single-page-summary/styles';
import { Row } from 'spw-shared/globalComponents';
import { colors } from 'spw-theme';
import { NO_DATA_TO_SHOW } from 'spw-constants';
import { selectAltWorkpointsBreakdown } from 'spw-redux/modules/program-summary/esa/selectors';
import { useSelector } from 'react-redux';
import { getColor, numberWithCommasOrDash } from 'spw-shared/utils';
import { PercentBar } from 'spw-components/collab/percent-bar';
import { AlternativeWorkpointsBreakdown } from 'spw-redux/modules/program-summary/types';
import { useSpaceTypeCategories } from 'spw-shared/hooks/use-space-type-categories';
import { projectColors as selectProjectColors } from 'spw-redux/modules/projects/selectors';
import { ProgramSummaryCardsContext } from '../summary-cards';

type CategoriesMap = ReturnType<typeof useSpaceTypeCategories>;

const formatCategoryPercentage = (percentage: number) =>
  percentage.toLocaleString('en', { maximumFractionDigits: 0 });

const AlternativeWorkpointsBreakdownCard = ({
  spaceTypeCategoriesByCategory,
}: {
  spaceTypeCategoriesByCategory: CategoriesMap;
}) => {
  const { showRsf } = useContext(ProgramSummaryCardsContext);
  const altWorkpointsBreakdown = useSelector(selectAltWorkpointsBreakdown);
  const totalAltWP = altWorkpointsBreakdown.all.seatsCount;
  const projectColors = useSelector(selectProjectColors);

  const getCategorySeats = (key: keyof AlternativeWorkpointsBreakdown) => {
    return (altWorkpointsBreakdown[key] || { seatsCount: 0 }).seatsCount;
  };

  const getCategoryPercentage = (key: keyof AlternativeWorkpointsBreakdown) => {
    return (getCategorySeats(key) / totalAltWP) * 100 || 0;
  };

  const renderCategoryRow = (
    key: keyof AlternativeWorkpointsBreakdown,
    { isLast } = { isLast: false }
  ) => {
    const category =
      spaceTypeCategoriesByCategory[`${key}Category` as keyof CategoriesMap];

    return (
      <BreakdownRow className={isLast ? 'last' : ''}>
        <BreakdownColumn item xs={8}>
          <Row alignItems="center">
            <Box
              width={12}
              height={12}
              borderRadius={6}
              bgcolor={getColor(
                category.defaultSpaceTypeCategory.category,
                projectColors
              )}
              mr={1}
            />
            {category.name}
          </Row>
        </BreakdownColumn>
        <BreakdownColumn item xs={4} className="right">
          {numberWithCommasOrDash(getCategorySeats(key))}
        </BreakdownColumn>
      </BreakdownRow>
    );
  };

  const renderCategoryBar = (key: keyof AlternativeWorkpointsBreakdown) => {
    const percentage = getCategoryPercentage(key);
    const category =
      spaceTypeCategoriesByCategory[`${key}Category` as keyof CategoriesMap];

    return (
      <Box display="flex" alignItems="center" mb={1}>
        <Box
          width="30px"
          fontSize="12px"
          lineHeight="16px"
          mr={1}
          textAlign="right"
        >
          {percentage ? formatCategoryPercentage(percentage) : NO_DATA_TO_SHOW}%
        </Box>
        <Box flex={1}>
          <PercentBar
            backgroundColor={colors.veryLightGray}
            completionColor={getColor(
              category.defaultSpaceTypeCategory.category,
              projectColors
            )}
            percentage={percentage}
            height="16px"
            width="100%"
          />
        </Box>
      </Box>
    );
  };

  return (
    <SmallCard $tall={showRsf} display="flex" flexDirection="column">
      <SmallCardTitle>Alternative Workpoints - Seats Breakdown</SmallCardTitle>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        flex={1}
      >
        <Grid container columnSpacing={4}>
          <Grid item xs={6}>
            {renderCategoryRow('individualWorkspaces')}
            {renderCategoryRow('meetingSpaces')}
            {renderCategoryRow('amenitySpaces')}
            {renderCategoryRow('supportSpaces', { isLast: true })}
            <BreakdownRow style={{ marginBottom: 0 }}>
              <BreakdownColumn item xs={9}>
                Total Alternative Workpoints
              </BreakdownColumn>
              <BreakdownColumn item xs={3} className="right">
                {numberWithCommasOrDash(altWorkpointsBreakdown.all.seatsCount)}
              </BreakdownColumn>
            </BreakdownRow>
          </Grid>
          <Grid item xs={6}>
            {renderCategoryBar('individualWorkspaces')}
            {renderCategoryBar('meetingSpaces')}
            {renderCategoryBar('amenitySpaces')}
            {renderCategoryBar('supportSpaces')}
          </Grid>
        </Grid>
      </Box>
    </SmallCard>
  );
};

export default AlternativeWorkpointsBreakdownCard;
